import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon, ChevronDownIcon } from 'lucide-react';
const ROLE_COLORS: Record<string, string> = {
  strategic: '#4B9FE1',
  program: '#34D399',
  technical: '#F59E0B',
  partner: '#A78BFA'
};
const ROLE_LABELS: Record<string, string> = {
  strategic: 'Strategic',
  program: 'Program',
  technical: 'Technical',
  partner: 'Partner'
};
interface QuestionBreakdown {
  questionNum: number;
  shortQuestion: string;
  userAnswer: string;
  roleResults: {
    roleId: string;
    percentage: number;
  }[];
}
const questionBreakdowns: QuestionBreakdown[] = [{
  questionNum: 1,
  shortQuestion: 'Greatest uncertainty in oncology drug development?',
  userAnswer: 'Understanding whether therapy reaches and affects tumours',
  roleResults: [{
    roleId: 'strategic',
    percentage: 48
  }, {
    roleId: 'program',
    percentage: 58
  }, {
    roleId: 'technical',
    percentage: 62
  }, {
    roleId: 'partner',
    percentage: 40
  }]
}, {
  questionNum: 2,
  shortQuestion: 'Best approach to improve early confidence?',
  userAnswer: 'Real-time biological insight using molecular imaging',
  roleResults: [{
    roleId: 'strategic',
    percentage: 42
  }, {
    roleId: 'program',
    percentage: 55
  }, {
    roleId: 'technical',
    percentage: 68
  }, {
    roleId: 'partner',
    percentage: 38
  }]
}, {
  questionNum: 3,
  shortQuestion: 'How are imaging priorities evolving?',
  userAnswer: 'Imaging is becoming central to translational drug development',
  roleResults: [{
    roleId: 'strategic',
    percentage: 65
  }, {
    roleId: 'program',
    percentage: 60
  }, {
    roleId: 'technical',
    percentage: 52
  }, {
    roleId: 'partner',
    percentage: 48
  }]
}, {
  questionNum: 4,
  shortQuestion: 'What matters most when selecting partners?',
  userAnswer: 'Integrated translational platforms',
  roleResults: [{
    roleId: 'strategic',
    percentage: 55
  }, {
    roleId: 'program',
    percentage: 48
  }, {
    roleId: 'technical',
    percentage: 35
  }, {
    roleId: 'partner',
    percentage: 62
  }]
}];
function RoleBar({
  roleId,
  percentage,
  delay
}: {
  roleId: string;
  percentage: number;
  delay: number;
}) {
  const color = ROLE_COLORS[roleId];
  const label = ROLE_LABELS[roleId];
  return <div className="flex items-center space-x-3" data-id="element-211">
      <span className="text-[10px] font-semibold uppercase tracking-wider w-[62px] text-right flex-shrink-0" style={{
      color
    }} data-id="element-212">
        {label}
      </span>
      <div className="flex-1 h-5 bg-white/5 rounded overflow-hidden relative" data-id="element-213">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${percentage}%`
      }} transition={{
        duration: 0.8,
        ease: 'easeOut',
        delay
      }} className="h-full rounded" style={{
        backgroundColor: color,
        opacity: 0.85
      }} data-id="element-214" />
      </div>
      <motion.span initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: delay + 0.5
    }} className="text-xs font-medium text-slate-300 w-[32px] text-right tabular-nums" data-id="element-215">
        {percentage}%
      </motion.span>
    </div>;
}
function QuestionCard({
  data,
  index
}: {
  data: QuestionBreakdown;
  index: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const baseDelay = 0.15 + index * 0.12;
  // Find the role with highest %, and the one with lowest to highlight the gap
  const sorted = [...data.roleResults].sort((a, b) => b.percentage - a.percentage);
  const gap = sorted[0].percentage - sorted[sorted.length - 1].percentage;
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: baseDelay
  }} className="bg-white/[0.03] border border-white/[0.08] rounded-xl overflow-hidden" data-id="element-216">
      {/* Header — always visible */}
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-start justify-between p-4 pb-3 text-left group" data-id="element-217">
        <div className="flex-1 min-w-0" data-id="element-218">
          <div className="flex items-center space-x-2 mb-1.5" data-id="element-219">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#4B9FE1]" data-id="element-220">
              Q{data.questionNum}
            </span>
            {gap >= 20 && <span className="text-[9px] font-medium uppercase tracking-wider text-amber-400/80 bg-amber-400/10 px-1.5 py-0.5 rounded" data-id="element-221">
                {gap}pt gap
              </span>}
          </div>
          <p className="text-sm text-slate-300 leading-snug pr-2" data-id="element-222">
            {data.shortQuestion}
          </p>
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-slate-500 flex-shrink-0 mt-1 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} data-id="element-223" />
      </button>

      {/* Expandable body */}
      {expanded && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2
    }} className="px-4 pb-4" data-id="element-224">
          {/* User's answer */}
          <div className="mb-4 flex items-start space-x-2" data-id="element-225">
            <div className="w-1.5 h-1.5 rounded-full bg-white/40 mt-1.5 flex-shrink-0" data-id="element-226" />
            <p className="text-xs text-slate-400 leading-relaxed" data-id="element-227">
              <span className="text-slate-500" data-id="element-228">Your answer: </span>
              <span className="text-white/70" data-id="element-229">{data.userAnswer}</span>
            </p>
          </div>

          {/* Role comparison bars */}
          <div className="space-y-2.5" data-id="element-230">
            {data.roleResults.map((result, i) => <RoleBar key={result.roleId} roleId={result.roleId} percentage={result.percentage} delay={baseDelay + 0.1 + i * 0.08} data-id="element-231" />)}
          </div>
        </motion.div>}
    </motion.div>;
}
export function SummarySlide({
  onNext,
  onBack,
  onSkip
}: {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  return <div className="flex flex-col h-full relative z-10 bg-[#0B1623]" data-id="element-232">
      {/* Fixed header */}
      <div className="px-6 pt-8 pb-4 flex-shrink-0" data-id="element-233">
        <div className="flex items-center justify-between mb-6" data-id="element-234">
          <button onClick={onBack} className="p-2 -ml-2 text-slate-500 hover:text-white transition-colors" aria-label="Go back" data-id="element-235">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-236" />
          </button>
          <div className="bg-white px-3 py-1.5 rounded-lg inline-block shadow-sm" data-id="element-237">
            <img src="https://cdn.magicpatterns.com/uploads/ifejLUcSApynCwwp5oN3Zr/Screenshot_2026-03-04_at_16.40.29.png" alt="Minerva Imaging" className="h-5 object-contain" data-id="element-238" />
          </div>
          <button onClick={onSkip} className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-2" data-id="element-239">
            Skip
          </button>
        </div>

        <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="text-2xl font-light text-white leading-tight mb-1" data-id="element-240">
          How roles compare
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.12
      }} className="text-sm text-slate-400" data-id="element-241">
          % who answered the same as you, by role
        </motion.p>
      </div>

      {/* Legend */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.18
    }} className="px-6 pb-4 flex flex-wrap gap-x-4 gap-y-1.5 flex-shrink-0" data-id="element-242">
        {Object.entries(ROLE_LABELS).map(([id, label]) => <div key={id} className="flex items-center space-x-1.5" data-id="element-243">
            <div className="w-2.5 h-2.5 rounded-sm" style={{
          backgroundColor: ROLE_COLORS[id]
        }} data-id="element-244" />
            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider" data-id="element-245">
              {label}
            </span>
          </div>)}
      </motion.div>

      {/* Scrollable question cards */}
      <div className="flex-1 overflow-y-auto px-6 pb-4 no-scrollbar space-y-3" data-id="element-246">
        {questionBreakdowns.map((q, i) => <QuestionCard key={q.questionNum} data={q} index={i} data-id="element-247" />)}
      </div>

      {/* Fixed footer button */}
      <div className="px-6 py-4 flex-shrink-0" data-id="element-248">
        <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.9
      }} onClick={onNext} className="w-full py-4 bg-[#4B9FE1] text-white rounded-xl font-medium flex items-center justify-center hover:bg-[#3A8BD0] transition-colors group focus:outline-none focus:ring-2 focus:ring-[#4B9FE1] focus:ring-offset-2 focus:ring-offset-[#0B1623]" data-id="element-249">
          Continue
          <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-250" />
        </motion.button>
      </div>
    </div>;
}